/*
 * Sensor de umidade no óleo (Treetech MO-2), construído EM CÓDIGO a partir da
 * referência autônoma em Three.js `feature/referencia-3d/MOS/gemini-code-
 * 1788975583227.html` — mesma peça (caixa do monitor com etiqueta, hastes,
 * corpo de válvula em latão e ponteira perfurada), só que com as cotas em
 * metros e usando as primitivas de geometriaHelpers.js, para render flutuando
 * acima do modelo do CAD (ver transformadorCad.js).
 *
 * Não é uma peça do contrato (não entra em `parts`/`anchors`): não existe
 * card do CMA nem peça correspondente no `.glb` para ela — é só um objeto
 * decorativo, adicionado direto no `group` do modelo.
 */
import * as THREE from 'three';
import {
  box, cyl, cylAxis, srgbColor,
} from './geometriaHelpers';
import { HIGHLIGHT_TARGET } from './contrato';

/*
 * Cotas em metros. A ponteira fica na origem (Y = 0) e o resto do sensor
 * cresce para cima a partir dela — é o que permite ao chamador posicionar
 * `group.position.y` no topo do ativo e obter a ponteira (a base do sensor)
 * exatamente nessa altura, com o corpo flutuando acima.
 */
const TIP_RADIUS = 0.03;
const TIP_HEIGHT = 0.16;
const HOLE_RADIUS = 0.008;
const HOLE_HEIGHT = 0.09;
const HOLE_Y_OFFSETS = [0.035, 0.075, 0.115];

const LOWER_ROD_RADIUS = 0.025;
const LOWER_ROD_HEIGHT = 0.42;

const THREAD_RADIUS = 0.05;
const THREAD_HEIGHT = 0.05;
const VALVE_BODY_RADIUS = 0.07;
const VALVE_BODY_HEIGHT = 0.11;
const SIDE_PORT_RADIUS = 0.045;
const SIDE_PORT_HEIGHT = 0.09;
const HEX_NUT_RADIUS = 0.06;
const HEX_NUT_HEIGHT = 0.06;

const UPPER_ROD_RADIUS = 0.028;
const UPPER_ROD_HEIGHT = 0.42;

const BOX_WIDTH = 0.26;
const BOX_HEIGHT = 0.46;
const BOX_DEPTH = 0.20;
const SCREW_RADIUS = 0.008;
const SCREW_HEIGHT = 0.008;

/** Velocidade de giro do sensor flutuando sobre o ativo, em radianos por
 *  segundo — mesma leitura de apresentação lenta e constante da referência
 *  (`sensorGroup.rotation.y += 0.005` por quadro). Consumida por
 *  `updateFans` (CmaAsset3dView.vue), que já gira qualquer objeto com
 *  `userData.giro`, hélice ou não. */
const GIRO = 0.3;

/** Etiqueta frontal da caixa do monitor, no mesmo layout da referência:
 *  fundo escuro, moldura e os dizeres do MO-2. Only roda no cliente (canvas
 *  2D), como as demais texturas do CMA (ver cmaAssetTextures.js). */
function criarTexturaEtiqueta() {
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 1024;
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = '#18191c';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  ctx.strokeStyle = '#33373e';
  ctx.lineWidth = 10;
  ctx.strokeRect(10, 10, canvas.width - 20, canvas.height - 20);

  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'center';

  ctx.font = 'bold 42px Arial';
  ctx.fillText('MO', canvas.width / 2, 120);

  ctx.font = 'bold 24px Arial';
  ctx.fillText('OIL MOISTURE MONITOR SENSOR', canvas.width / 2, 180);

  ctx.font = '20px Arial';
  ctx.fillStyle = '#aaaaaa';
  ctx.fillText('Model: MO-2', canvas.width / 2, 220);
  ctx.fillText('Functions: H2O - Temp.', canvas.width / 2, 255);
  ctx.fillText('Power Supply: 85-265 Vdc / Vac', canvas.width / 2, 290);

  ctx.font = '18px Arial';
  ctx.fillText('Serial number: 1234567', canvas.width / 2, 350);

  ctx.font = 'bold 36px Arial';
  ctx.fillStyle = '#ffffff';
  ctx.fillText('Treetech', canvas.width / 2, 480);

  ctx.font = '18px Arial';
  ctx.fillStyle = '#888888';
  ctx.fillText('TREETECH TECNOLOGIA LTDA', canvas.width / 2, 780);
  ctx.fillText('Made in Brazil', canvas.width / 2, 820);

  return new THREE.CanvasTexture(canvas);
}

/**
 * Monta o sensor MO-2. Devolve o `THREE.Group` já com `userData.giro`
 * (ver GIRO) — quem chama só precisa posicioná-lo e, se quiser o giro,
 * empurrá-lo para `model.helices`.
 */
export function criarSensorMo2() {
  const metallicMaterial = new THREE.MeshStandardMaterial({
    color: srgbColor(0xd0d5dd), metalness: 0.9, roughness: 0.2,
  });
  const brassMaterial = new THREE.MeshStandardMaterial({
    color: srgbColor(0x999988), metalness: 0.8, roughness: 0.35,
  });
  const bodyBlackMaterial = new THREE.MeshStandardMaterial({
    color: srgbColor(0x1f2124), metalness: 0.3, roughness: 0.4,
  });
  const tipMaterial = new THREE.MeshStandardMaterial({
    color: srgbColor(0xb0b5bc), metalness: 0.7, roughness: 0.3,
  });
  const holeMaterial = new THREE.MeshBasicMaterial({ color: srgbColor(0x111111) });
  const frontFaceMaterial = new THREE.MeshStandardMaterial({
    map: criarTexturaEtiqueta(), metalness: 0.2, roughness: 0.3,
  });

  const sensor = new THREE.Group();

  // Ponteira sensora perfurada — a base do sensor, em Y = 0.
  const tip = cyl(TIP_RADIUS, TIP_HEIGHT, tipMaterial, 0, TIP_HEIGHT / 2, 0, 20);
  sensor.add(tip);
  HOLE_Y_OFFSETS.forEach((y) => {
    sensor.add(cylAxis(HOLE_RADIUS, HOLE_HEIGHT, holeMaterial, 0, y, 0, 'x', 10));
    sensor.add(cylAxis(HOLE_RADIUS, HOLE_HEIGHT, holeMaterial, 0, y, 0, 'z', 10));
  });

  // Haste metálica inferior.
  const lowerRodY = TIP_HEIGHT + LOWER_ROD_HEIGHT / 2;
  const lowerRod = cyl(LOWER_ROD_RADIUS, LOWER_ROD_HEIGHT, metallicMaterial, 0, lowerRodY, 0, 20);
  lowerRod.castShadow = true;
  sensor.add(lowerRod);

  // Corpo da válvula / conector central, em latão.
  const lowerRodTop = TIP_HEIGHT + LOWER_ROD_HEIGHT;
  const threadY = lowerRodTop + THREAD_HEIGHT / 2;
  sensor.add(cyl(THREAD_RADIUS, THREAD_HEIGHT, brassMaterial, 0, threadY, 0, 20));

  const valveBodyY = lowerRodTop + THREAD_HEIGHT + VALVE_BODY_HEIGHT / 2;
  sensor.add(cyl(VALVE_BODY_RADIUS, VALVE_BODY_HEIGHT, brassMaterial, 0, valveBodyY, 0, 24));

  const sidePortX = VALVE_BODY_RADIUS + SIDE_PORT_HEIGHT / 2;
  sensor.add(cylAxis(
    SIDE_PORT_RADIUS, SIDE_PORT_HEIGHT, brassMaterial, sidePortX, valveBodyY, 0, 'x', 8,
  ));

  const valveTop = lowerRodTop + THREAD_HEIGHT + VALVE_BODY_HEIGHT;
  const hexNutY = valveTop + HEX_NUT_HEIGHT / 2;
  sensor.add(cyl(HEX_NUT_RADIUS, HEX_NUT_HEIGHT, brassMaterial, 0, hexNutY, 0, 6));

  // Haste metálica superior.
  const valveGroupTop = valveTop + HEX_NUT_HEIGHT;
  const upperRodY = valveGroupTop + UPPER_ROD_HEIGHT / 2;
  const upperRod = cyl(UPPER_ROD_RADIUS, UPPER_ROD_HEIGHT, metallicMaterial, 0, upperRodY, 0, 20);
  upperRod.castShadow = true;
  sensor.add(upperRod);

  // Caixa superior (corpo do monitor), com a etiqueta na face frontal (+Z) —
  // ordem de materiais do BoxGeometry: [direita, esquerda, topo, base, frente, trás].
  const boxTop = valveGroupTop + UPPER_ROD_HEIGHT + BOX_HEIGHT;
  const bodyY = valveGroupTop + UPPER_ROD_HEIGHT + BOX_HEIGHT / 2;
  const bodyMesh = new THREE.Mesh(
    new THREE.BoxGeometry(BOX_WIDTH, BOX_HEIGHT, BOX_DEPTH),
    [
      bodyBlackMaterial, bodyBlackMaterial, bodyBlackMaterial,
      bodyBlackMaterial, frontFaceMaterial, bodyBlackMaterial,
    ],
  );
  bodyMesh.position.set(0, bodyY, 0);
  bodyMesh.castShadow = true;
  /* Marca só a caixa como alvo do realce da âncora 'gas' (ver
   * highlightPart/HIGHLIGHT_TARGET em CmaAsset3dView.vue) — mesma convenção
   * do tanque/buchas: tingir as 19 peças do sensor inteiro (incluindo os
   * furos da ponteira, que ficam coincidentes/sobrepostos com o cilindro
   * dela) causava z-fighting entre elas ao ganhar a MESMA cor emissiva, o
   * suficiente pra parecer que o sensor "sumia" no hover. */
  bodyMesh.name = HIGHLIGHT_TARGET;
  sensor.add(bodyMesh);

  // Parafusos do topo da caixa.
  const screwY = boxTop - 0.02;
  [-1, 1].forEach((sx) => {
    [-1, 1].forEach((sz) => {
      sensor.add(box(
        SCREW_RADIUS * 2, SCREW_HEIGHT, SCREW_RADIUS * 2, metallicMaterial,
        sx * (BOX_WIDTH / 2 - 0.03), screwY, sz * (BOX_DEPTH / 2 - 0.03),
      ));
    });
  });

  sensor.userData.giro = GIRO;
  /* Altura (em Y local) da etiqueta na caixa do monitor — é onde a âncora
   * 'gas' do card Gás e Umidade mira (ver criarModeloCad em
   * transformadorCad.js), em vez de repetir a conta das cotas acima. */
  sensor.userData.alturaEtiqueta = bodyY;
  return sensor;
}

export default criarSensorMo2;
